import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from './shared/Icon';

const MESSAGE_ICONS = [
  'users',
  'trendUp',
  'flag',
  'pin',
  'activity',
  'star',
  'table',
  'check',
] as const;

// Heights for the animated bar chart that "builds" while loading. The wave is
// driven purely by CSS keyframes; these are just the resting silhouette.
const BARS = [38, 64, 49, 82, 58, 95, 71, 88, 54, 76, 62, 90];

export function PerformanceLoader({
  label,
  onCancel,
}: {
  label?: string;
  /**
   * Stop waiting and go back to the screen the change replaced (its settings
   * are put back; their data is still cached). Absent on a cold load.
   */
  onCancel?: () => void;
}) {
  const { t } = useTranslation('performance');
  const [idx, setIdx] = useState(0);
  const messages = MESSAGE_ICONS.map((icon, i) => ({
    icon,
    text: t(`loader.m${i}`),
  }));

  useEffect(() => {
    const id = setInterval(() => {
      setIdx(i => (i + 1) % MESSAGE_ICONS.length);
    }, 2100);
    return () => clearInterval(id);
  }, []);

  const bars = useMemo(() => BARS, []);
  const current = messages[idx];

  return (
    <div className="perf-loader" role="status" aria-live="polite">
      <div className="pl-card">
        <div className="pl-aurora" aria-hidden />

        <div className="pl-chart" aria-hidden>
          {bars.map((h, i) => (
            <span
              key={i}
              className="pl-bar"
              style={{
                // Resting height plus a per-bar phase offset so the wave reads
                // as a chart populating left-to-right rather than pulsing in unison.
                ['--h' as string]: `${h}%`,
                ['--d' as string]: `${i * 0.11}s`,
              }}
            />
          ))}
          <span className="pl-sweep" />
        </div>

        <div className="pl-headline">
          <span className="pl-spin">
            <Icon name="activity" size={16} stroke={2.4} />
          </span>
          {label ?? t('loader.readingRoom')}
        </div>

        <div className="pl-rotator">
          {/* key forces the enter animation to replay on every message swap */}
          <div className="pl-msg" key={idx}>
            <span className="pl-msg-ic">
              <Icon name={current.icon} size={14} stroke={2.2} />
            </span>
            <span className="pl-msg-tx">{current.text}</span>
          </div>
        </div>

        <div className="pl-track" aria-hidden>
          <span className="pl-track-fill" />
        </div>

        <div className="pl-dots" aria-hidden>
          {messages.map((_, i) => (
            <span key={i} className={`pl-dot${i === idx ? ' on' : ''}`} />
          ))}
        </div>

        {onCancel ? (
          <button type="button" className="pl-cancel" onClick={onCancel}>
            {t('loader.cancel')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
